import { type FC, type RefObject } from "react";
import { type UseFormRegister, type UseFormWatch, type FieldErrors } from "react-hook-form";
import { SectionTitle } from "@userFrontend/components/SectionTitle";
import { GuidanceBox } from "@root/shared/components/GuidanceBox";
import { FormItemName } from "@root/shared/components/FormItemName";
import { TextField } from "@root/shared/components/TextField";
import { SectionWithTitleAndLabel } from "@userFrontend/components/SectionWithTitleAndLabel";
import { ErrorInformationText } from "@userFrontend/components/ErrorInformationText";
import { type ViolationReportInputsValues } from "../../schema";
import * as styles from "./styles.css";

type Props = {
  // React Hook Form の register 関数
  register: UseFormRegister<ViolationReportInputsValues>;
  // React Hook Form の watch 関数
  watch: UseFormWatch<ViolationReportInputsValues>;
  // フォームのエラー情報
  errors: FieldErrors<ViolationReportInputsValues>;
  // セクションタイトル領域への ref（エラー時のスクロール用）
  containerRef?: RefObject<HTMLDivElement>;
};

/**
 * 連絡先メールアドレス確認コンポーネント
 * - 登録中のメールアドレスを表示
 * - 別のメールアドレスを希望する場合は入力可能
 */
export const EmailConfirmation: FC<Props> = ({ containerRef, register, watch, errors }) => {
  // 登録中のメールアドレスを取得
  const registeredEmail = watch("registeredEmail") || "";

  return (
    <>
      <SectionTitle text="ご連絡先の確認" />
      <div className={styles.wrapper}>
        <SectionWithTitleAndLabel
          isRequired={false}
          title="ご連絡する際のメールアドレスは、以下でよろしいですか？"
          containerRef={containerRef ?? null}
        >
          <GuidanceBox themeColor="gray" className={styles.emailDisplay}>
            <span className={styles.emailLabel}>登録中のメールアドレス</span>
            <span>{registeredEmail}</span>
          </GuidanceBox>
          <div className={styles.inputSection}>
            <FormItemName labelRequirementType="optional">
              他のメールアドレスを希望する場合
            </FormItemName>
            <div className={styles.inputWrapper}>
              <TextField
                type="email"
                {...register("alternativeEmail")}
                placeholder="入力してください"
                hasError={!!errors.alternativeEmail}
              />
              {errors.alternativeEmail?.message && (
                <ErrorInformationText text={errors.alternativeEmail.message} />
              )}
            </div>
          </div>
        </SectionWithTitleAndLabel>
      </div>
    </>
  );
};
